import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from '@/components/ui/toast/useToast';

export interface CartItem {
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  unit: string;
  availableStock?: number; // Add this to track available stock
}

interface DraftInvoiceStore {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  getTotalItems: () => number;
}

export const useDraftInvoiceStore = create<DraftInvoiceStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) =>
        set((state) => {
          const existingIndex = state.items.findIndex((i) => i.productId === item.productId);

          if (existingIndex >= 0) {
            // Update quantity if item exists
            const existingItem = state.items[existingIndex];
            const newQuantity = existingItem.quantity + item.quantity;
            
            // Check stock availability
            if (item.availableStock !== undefined && newQuantity > item.availableStock) {
              toast.error(
                'مخزون غير كافٍ / Stock insuffisant',
                `${item.productName}: Stock disponible ${item.availableStock}, Quantité demandée ${newQuantity}`
              );
              return state; // Don't update - NO SUCCESS TOAST
            }
            
            const newItems = [...state.items];
            newItems[existingIndex].quantity = newQuantity;
            toast.success('تم التحديث / Mis à jour', `Quantité: ${newQuantity}`);
            return { items: newItems };
          } else {
            // Add new item - check stock
            if (item.availableStock !== undefined && item.quantity > item.availableStock) {
              toast.error(
                'مخزون غير كافٍ / Stock insuffisant',
                `${item.productName}: Stock disponible ${item.availableStock}, Quantité demandée ${item.quantity}`
              );
              return state; // Don't add - NO SUCCESS TOAST
            }
            
            toast.success('تمت الإضافة / Ajouté', item.productName);
            return { items: [...state.items, item] };
          }
        }),

      updateQuantity: (productId, quantity) =>
        set((state) => {
          const item = state.items.find((i) => i.productId === productId);
          
          // Check stock if available
          if (item?.availableStock !== undefined && quantity > item.availableStock) {
            toast.error(
              'مخزون غير كافٍ / Stock insuffisant',
              `Stock disponible: ${item.availableStock}`
            );
            return state; // Don't update
          }
          
          return {
            items: state.items.map((item) =>
              item.productId === productId ? { ...item, quantity } : item
            ),
          };
        }),

      removeItem: (productId) =>
        set((state) => ({
          items: state.items.filter((item) => item.productId !== productId),
        })),

      clearCart: () => set({ items: [] }),

      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
    }),
    {
      name: 'draft-invoice-storage',
    }
  )
);
